import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import AntLocalization from '../../utils/AntLocalization';
import './index.scss';

var t = function (obj) { return AntLocalization.translateText(obj); };

var IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff'];
var PDF_EXTS = ['pdf'];
var VIDEO_EXTS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
var OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv'];

function getExt(file) {
    if (!file) return '';
    return (file.name || file.url || '').split('.').pop().split('?')[0].toLowerCase();
}

function getType(file) {
    if (!file) return 'other';
    var ext = getExt(file);
    var mime = (file.type || '').toLowerCase();
    if (IMAGE_EXTS.indexOf(ext) >= 0 || mime.indexOf('image') === 0) return 'image';
    if (PDF_EXTS.indexOf(ext) >= 0 || mime === 'application/pdf') return 'pdf';
    if (VIDEO_EXTS.indexOf(ext) >= 0 || mime.indexOf('video') === 0) return 'video';
    if (OFFICE_EXTS.indexOf(ext) >= 0 || mime.indexOf('word') >= 0 || mime.indexOf('spreadsheet') >= 0 || mime.indexOf('excel') >= 0 || mime.indexOf('presentation') >= 0 || mime.indexOf('powerpoint') >= 0) return 'office';
    return 'other';
}

function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}

// ─── Image Viewer (zoom + rotate + pan) ──────────────────────────
function ImageViewer(props) {
    var file = props.file;
    var [zoom, setZoom] = useState(1);
    var [rotation, setRotation] = useState(0);
    var [pos, setPos] = useState({ x: 0, y: 0 });
    var [dragging, setDragging] = useState(false);
    var dragRef = useRef({ sx: 0, sy: 0, px: 0, py: 0 });

    useEffect(function () { setZoom(1); setRotation(0); setPos({ x: 0, y: 0 }); }, [file]);

    var onWheel = useCallback(function (e) {
        e.preventDefault();
        setZoom(function (z) { return Math.max(0.1, Math.min(10, z + (e.deltaY < 0 ? 0.15 : -0.15))); });
    }, []);
    var onDown = useCallback(function (e) {
        if (e.button !== 0) return; e.preventDefault(); setDragging(true);
        dragRef.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
    }, [pos]);
    var onMove = useCallback(function (e) {
        if (!dragging) return;
        setPos({ x: dragRef.current.px + e.clientX - dragRef.current.sx, y: dragRef.current.py + e.clientY - dragRef.current.sy });
    }, [dragging]);
    var onUp = useCallback(function () { setDragging(false); }, []);
    useEffect(function () {
        if (!dragging) return;
        window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
        return function () { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [dragging, onMove, onUp]);

    return (
        <div className="fv-image-wrap">
            <div className={'fv-image-area' + (dragging ? ' grabbing' : '')} onWheel={onWheel} onMouseDown={onDown}>
                <img src={file.url} alt={file.name || ''} draggable={false}
                    style={{ transform: 'translate(' + pos.x + 'px,' + pos.y + 'px) scale(' + zoom + ') rotate(' + rotation + 'deg)' }} />
            </div>
            <div className="fv-image-controls">
                <button onClick={function () { setZoom(function (z) { return Math.min(10, z + 0.25); }); }}>+</button>
                <span className="fv-zoom-level">{Math.round(zoom * 100)}%</span>
                <button onClick={function () { setZoom(function (z) { return Math.max(0.1, z - 0.25); }); }}>−</button>
                <span className="fv-ctrl-sep" />
                <button onClick={function () { setRotation(function (r) { return r - 90; }); }}>↺</button>
                <button onClick={function () { setRotation(function (r) { return r + 90; }); }}>↻</button>
                <span className="fv-ctrl-sep" />
                <button onClick={function () { setZoom(1); setRotation(0); setPos({ x: 0, y: 0 }); }}>⟲</button>
            </div>
        </div>
    );
}

// ─── Doc Viewer (PDF + Office) ───────────────────────────────────
function DocViewer(props) {
    var file = props.file;
    var [loading, setLoading] = useState(true);
    var [timedOut, setTimedOut] = useState(false);
    var googleUrl = 'https://docs.google.com/gview?url=' + encodeURIComponent(file.url) + '&embedded=true';

    useEffect(function () {
        setLoading(true); setTimedOut(false);
        var timer = setTimeout(function () { setTimedOut(true); }, 15000);
        return function () { clearTimeout(timer); };
    }, [file]);

    return (
        <div className="fv-iframe-wrap">
            {loading && (
                <div className="fv-doc-loading">
                    <div className="fv-doc-spinner" />
                    <span>{t({ vi: 'Đang tải tài liệu...', en: 'Loading document...', fr: 'Chargement du document...', es: 'Cargando documento...', id: 'Memuat dokumen...', ja: '文書を読み込み中...', zh: '文档加载中...', ko: '문서 불러오는 중...', hi: 'दस्तावेज़ लोड हो रहा...', ar: 'جارٍ تحميل المستند...' })}</span>
                    {timedOut && (
                        <a className="fv-doc-fallback-link" href={file.url} target="_blank" rel="noopener noreferrer">
                            {t({ vi: 'Tải quá lâu? Ấn để mở trực tiếp', en: 'Taking too long? Click to open directly', fr: 'Trop long ? Cliquez pour ouvrir directement', es: '¿Tarda mucho? Clic para abrir directamente', id: 'Terlalu lama? Klik untuk buka langsung', ja: '時間がかかる場合はクリックで直接開く', zh: '加载太久？点击直接打开', ko: '너무 오래 걸리나요? 클릭하여 바로 열기', hi: 'देर लग रही है? सीधे खोलने हेतु क्लिक करें', ar: 'يستغرق وقتًا طويلًا؟ انقر للفتح مباشرة' })}
                        </a>
                    )}
                </div>
            )}
            <iframe src={googleUrl} title={file.name || 'Document'}
                style={loading ? { opacity: 0, position: 'absolute' } : undefined}
                onLoad={function () { setLoading(false); }} />
        </div>
    );
}

// ─── Render nội dung theo loại file ──────────────────────────────
function FileContent(props) {
    var file = props.file;
    var type = getType(file);

    if (type === 'image') return <ImageViewer file={file} />;

    if (type === 'pdf' || type === 'office') return <DocViewer file={file} />;


    if (type === 'video') {
        return (
            <div className="fv-video-wrap">
                <video controls src={file.url}>{t({ vi: 'Không hỗ trợ video', en: 'Video not supported', fr: 'Vidéo non supportée', es: 'Video no compatible', id: 'Video tidak didukung', ja: '動画は非対応です', zh: '不支持视频', ko: '동영상 미지원', hi: 'वीडियो समर्थित नहीं', ar: 'الفيديو غير مدعوم' })}</video>
            </div>
        );
    }

    // Fallback — hiển thị thông tin file + nút tải
    var ext = getExt(file);
    var icon = ext === 'pdf' ? '📄' : ext === 'doc' || ext === 'docx' ? '📝' : ext === 'xls' || ext === 'xlsx' ? '📊' : ext === 'zip' || ext === 'rar' ? '📦' : '📁';
    return (
        <div className="fv-fallback">
            <div className="fv-fallback-icon">{icon}</div>
            <div className="fv-fallback-name">{file.name || t({ vi: 'Tệp không xác định', en: 'Unknown file', fr: 'Fichier inconnu', es: 'Archivo desconocido', id: 'Berkas tidak dikenal', ja: '不明なファイル', zh: '未知文件', ko: '알 수 없는 파일', hi: 'अज्ञात फ़ाइल', ar: 'ملف غير معروف' })}</div>
            {file.size ? <div className="fv-fallback-size">{formatSize(file.size)}</div> : null}
            <a className="fv-fallback-download" href={file.url} target="_blank" rel="noopener noreferrer">
                {t({ vi: 'Tải xuống', en: 'Download', fr: 'Télécharger', es: 'Descargar', id: 'Unduh', ja: 'ダウンロード', zh: '下载', ko: '다운로드', hi: 'डाउनलोड', ar: 'تنزيل' })}
            </a>
        </div>
    );
}

// ─── Thumbnail strip ─────────────────────────────────────────────
function ThumbnailStrip(props) {
    var activeRef = useRef(null);
    useEffect(function () {
        if (activeRef.current) activeRef.current.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }, [props.currentIndex]);
    return (
        <div className="fv-thumbnails">
            {props.files.map(function (f, i) {
                var type = getType(f);
                var isActive = i === props.currentIndex;
                var label = type === 'image' ? null : type === 'pdf' ? 'PDF' : type === 'video' ? 'VID' : type === 'office' ? getExt(f).toUpperCase() : 'FILE';
                return (
                    <div key={i} ref={isActive ? activeRef : null} className={'fv-thumb' + (isActive ? ' active' : '')} onClick={function () { props.onSelect(i); }}>
                        {type === 'image' ? <img src={f.url} alt="" draggable={false} /> : <div className="fv-thumb-label"><span>{label}</span></div>}
                    </div>
                );
            })}
        </div>
    );
}

// ═══ MAIN COMPONENT ═════════════════════════════════════════════
export default function AntFileViewer(props) {
    var visible = props.visible;
    var onClose = props.onClose;
    var filesProp = props.files || (props.file ? [props.file] : []);
    var initialIndex = props.initialIndex || 0;

    var files = filesProp;
    var isGallery = files.length > 1;

    var [idx, setIdx] = useState(initialIndex);
    useEffect(function () { setIdx(initialIndex || 0); }, [initialIndex, visible]);

    var file = files[idx] || files[0];

    var goNext = useCallback(function () { if (isGallery) setIdx(function (i) { return (i + 1) % files.length; }); }, [isGallery, files.length]);
    var goPrev = useCallback(function () { if (isGallery) setIdx(function (i) { return (i - 1 + files.length) % files.length; }); }, [isGallery, files.length]);

    useEffect(function () {
        if (!visible) return;
        var onKey = function (e) {
            if (e.key === 'Escape' && onClose) onClose();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
        };
        window.addEventListener('keydown', onKey);
        return function () { window.removeEventListener('keydown', onKey); };
    }, [visible, onClose, goNext, goPrev]);

    if (!visible || !files.length || !file) return null;

    return ReactDOM.createPortal(
        <div className="fv-overlay" onClick={function (e) { if (e.target === e.currentTarget && onClose) onClose(); }}>
            {/* Toolbar trên cùng */}
            <div className="fv-toolbar">
                <div className="fv-toolbar-left">
                    <span className="fv-filename">{file.name || t({ vi: 'Tệp', en: 'File', id: 'Berkas', zh: '文件', ko: '파일', hi: 'फ़ाइल', ar: 'ملف', fr: 'Fichier', es: 'Archivo', ja: '文書' })}</span>
                    {file.size ? <span className="fv-filesize">{formatSize(file.size)}</span> : null}
                    {isGallery && <span className="fv-counter">{idx + 1} / {files.length}</span>}
                </div>
                <div className="fv-toolbar-right">
                    <a className="fv-tool-btn" href={file.url} target="_blank" rel="noopener noreferrer"
                        title={t({ vi: 'Tải xuống', en: 'Download', fr: 'Télécharger', es: 'Descargar', id: 'Unduh', ja: 'ダウンロード', zh: '下载', ko: '다운로드', hi: 'डाउनलोड', ar: 'تنزيل' })} onClick={function (e) { e.stopPropagation(); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                    </a>
                    <button className="fv-tool-btn fv-close-btn" onClick={onClose} title={t({ vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' })}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                    </button>
                </div>
            </div>

            {/* Nội dung chính */}
            <div className="fv-content">
                <FileContent file={file} />
            </div>

            {/* Nút prev/next 2 bên */}
            {isGallery && (
                <button className="fv-nav fv-nav-prev" onClick={function (e) { e.stopPropagation(); goPrev(); }} title={t({ vi: 'Trước', en: 'Previous', fr: 'Précédent', es: 'Anterior', id: 'Sebelumnya', ja: '前へ', zh: '上一个', ko: '이전', hi: 'पिछला', ar: 'السابق' })}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 19l-7-7 7-7" /></svg>
                </button>
            )}
            {isGallery && (
                <button className="fv-nav fv-nav-next" onClick={function (e) { e.stopPropagation(); goNext(); }} title={t({ vi: 'Sau', en: 'Next', fr: 'Suivant', es: 'Próximo', id: 'Berikut', ja: '次へ', zh: '下一个', ko: '다음', hi: 'अगला', ar: 'التالي' })}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 5l7 7-7 7" /></svg>
                </button>
            )}

            {/* Thumbnail strip dưới cùng */}
            {isGallery && (
                <ThumbnailStrip files={files} currentIndex={idx} onSelect={setIdx} />
            )}
        </div>,
        document.body
    );
}
